import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  CalendarClock,
  Loader2,
  RefreshCcw,
  Search,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DateRangeFilter } from "@/components/ui/date-range-filter";
import { FilterMenu } from "@/components/ui/filter-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminAppointmentList } from "@/hooks/useStaffAppointments";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";
import type {
  AdminAppointmentFilterOption,
  AdminAppointmentListItem,
} from "@/types/appointments";
import { AppointmentDetailsDialog } from "./AppointmentDetailsDialog";
import {
  formatDateForAPI,
  formatShortDate,
  formatTime,
  toTitleCase,
} from "./utils";

type AppointmentTab = "upcoming" | "ongoing" | "history";
type SortField = "patient" | "doctor" | "date" | "status";
type SortDirection = "asc" | "desc";
type StatusFilter = "all" | "confirmed" | "completed" | "cancelled" | "pending";

const PAGE_SIZE = 20;

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "pending", label: "Pending" },
];

const appointmentStatusClass = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "border-success/30 bg-success text-success dark:border-success/60 dark:bg-success/40 dark:text-success";
    case "cancelled":
    case "invalidated":
      return "border-destructive/30 bg-destructive text-destructive dark:border-destructive/60 dark:bg-destructive/40 dark:text-destructive";
    // theme-drift: decorative
    case "confirmed":
      return "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200";
    case "soft_reserved":
      return "border-warning/30 bg-warning/10 text-warning";
    // theme-drift: decorative
    default:
      return "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-200";
  }
};

const getStatusLabel = (status: string) => {
  if (status === "soft_reserved") return "Pending";
  if (status === "invalidated") return "Expired";
  return toTitleCase(status);
};

const getOrderingParam = (field: SortField, dir: SortDirection) => {
  const prefix = dir === "desc" ? "-" : "";
  return `${prefix}${field === "date" ? "date" : field}`;
};

const getStatusFilterParam = (status: StatusFilter) => {
  if (status === "all") return undefined;
  if (status === "pending") return "soft_reserved";
  return status;
};

const buildFilterOptions = (
  options?: AdminAppointmentFilterOption[],
  label = "All"
) => [
  { value: "all", label },
  ...(options ?? []).map((option) => ({
    value: option.id,
    label: option.label,
  })),
];

const SortButton = ({
  label,
  isActive,
  direction,
  onClick,
  className,
}: {
  label: string;
  isActive: boolean;
  direction: SortDirection;
  onClick: () => void;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "inline-flex items-center gap-1.5 text-left z-label uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
  >
    <span>{label}</span>
    {isActive ? (
      direction === "asc" ? (
        <ArrowUp className="h-3.5 w-3.5" />
      ) : (
        <ArrowDown className="h-3.5 w-3.5" />
      )
    ) : (
      <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />
    )}
  </button>
);

const LoadingRows = ({ showDoctor }: { showDoctor: boolean }) => (
  <>
    {Array.from({ length: 8 }).map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-20" />
          </div>
        </TableCell>
        {showDoctor ? (
          <TableCell className="hidden lg:table-cell">
            <Skeleton className="h-4 w-28" />
          </TableCell>
        ) : null}
        <TableCell className="hidden lg:table-cell">
          <Skeleton className="h-4 w-32" />
        </TableCell>
        <TableCell>
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-20" />
          </div>
        </TableCell>
        <TableCell>
          <Skeleton className="h-5 w-20 rounded-full" />
        </TableCell>
      </TableRow>
    ))}
  </>
);

export const AppointmentListPage = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<AppointmentTab>("upcoming");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [doctorFilter, setDoctorFilter] = useState<string>("all");
  const [serviceFilter, setServiceFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const [page, setPage] = useState(1);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<
    string | null
  >(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const deferredSearch = useDeferredValue(search.trim());

  const adminParams = useMemo(
    () => ({
      tab: activeTab,
      search: deferredSearch || undefined,
      doctor_id: doctorFilter !== "all" ? doctorFilter : undefined,
      service_id: serviceFilter !== "all" ? serviceFilter : undefined,
      status: getStatusFilterParam(statusFilter),
      start_date: dateRange?.from
        ? formatDateForAPI(dateRange.from)
        : undefined,
      end_date: dateRange?.to ? formatDateForAPI(dateRange.to) : undefined,
      ordering: getOrderingParam(sortField, sortDir),
      page,
      page_size: PAGE_SIZE,
    }),
    [
      activeTab,
      dateRange?.from,
      dateRange?.to,
      deferredSearch,
      doctorFilter,
      page,
      serviceFilter,
      sortDir,
      sortField,
      statusFilter,
    ]
  );

  const admin = useAdminAppointmentList(adminParams);

  useEffect(() => {
    setPage(1);
  }, [
    activeTab,
    deferredSearch,
    doctorFilter,
    serviceFilter,
    statusFilter,
    sortField,
    sortDir,
    dateRange?.from,
    dateRange?.to,
  ]);

  const doctorOptions = useMemo(
    () => buildFilterOptions(admin.data?.filters.doctors, "All doctors"),
    [admin.data?.filters.doctors]
  );
  const serviceOptions = useMemo(
    () => buildFilterOptions(admin.data?.filters.services, "All services"),
    [admin.data?.filters.services]
  );

  const adminError =
    admin.error instanceof Error
      ? admin.error.message
      : admin.error
        ? "Failed to load appointments"
        : null;

  const appointments: AdminAppointmentListItem[] = admin.data?.results ?? [];
  const isLoading = admin.isLoading;
  const isFetching = admin.isFetching;
  const refetch = admin.refetch;
  const tabCounts = admin.data?.tab_counts ?? {
    upcoming: 0,
    ongoing: 0,
    history: 0,
  };
  const totalCount = admin.data?.count ?? 0;

  const hasActiveFilters =
    statusFilter !== "all" ||
    doctorFilter !== "all" ||
    serviceFilter !== "all" ||
    !!dateRange?.from;

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const clearFilters = () => {
    setStatusFilter("all");
    setDoctorFilter("all");
    setServiceFilter("all");
    setDateRange(undefined);
    setPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border-border/60">
        <CardHeader className="gap-4 border-b border-border/60 bg-card p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
                onClick={() => navigate("/settings/appointments/calendar")}
                aria-label="Back to calendar"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <CalendarClock className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="z-h1 text-foreground">
                Appointment List
              </CardTitle>
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetch()}
                className="h-9 w-9 rounded-xl border-border/70 bg-background/80 shadow-none"
                aria-label="Refresh appointments"
              >
                {isFetching && !isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
              <div className="relative w-full md:w-[260px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search appointments..."
                  className="h-10 rounded-xl border-border/70 bg-background/85 pl-9 shadow-none z-body"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <FilterMenu
                  label="Doctor"
                  options={doctorOptions}
                  value={doctorFilter}
                  onValueChange={setDoctorFilter}
                  triggerClassName="border-border/70 bg-background px-3 shadow-none hover:bg-accent/20"
                  contentClassName="border-border/70 bg-popover shadow-md"
                />

                <FilterMenu
                  label="Service"
                  options={serviceOptions}
                  value={serviceFilter}
                  onValueChange={setServiceFilter}
                  triggerClassName="border-border/70 bg-background px-3 shadow-none hover:bg-accent/20"
                  contentClassName="border-border/70 bg-popover shadow-md"
                />

                <FilterMenu
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onValueChange={(value) =>
                    setStatusFilter(value as StatusFilter)
                  }
                  triggerClassName="border-border/70 bg-background px-3 shadow-none hover:bg-accent/20"
                  contentClassName="border-border/70 bg-popover shadow-md"
                />

                <DateRangeFilter
                  value={dateRange}
                  onChange={setDateRange}
                  placeholder="Date range"
                  triggerClassName="border-border/70 bg-background px-3"
                  contentClassName="border-border/70 bg-popover shadow-md"
                />

                {hasActiveFilters ? (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={clearFilters}
                    className="h-9 w-9 rounded-xl border-border/70 bg-background/80 text-muted-foreground shadow-none hover:text-foreground"
                    title="Clear all filters"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>

            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                setActiveTab(value as AppointmentTab);
                clearFilters();
                setSearch("");
              }}
              className="shrink-0 self-start lg:self-auto"
            >
              <TabsList>
                <TabsTrigger value="upcoming" className="gap-1.5">
                  Upcoming
                  {tabCounts.upcoming != null ? (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 z-caption z-numeric text-muted-foreground">
                      {tabCounts.upcoming}
                    </span>
                  ) : null}
                </TabsTrigger>
                <TabsTrigger value="ongoing" className="gap-1.5">
                  Ongoing
                  {tabCounts.ongoing != null ? (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 z-caption z-numeric text-muted-foreground">
                      {tabCounts.ongoing}
                    </span>
                  ) : null}
                </TabsTrigger>
                <TabsTrigger value="history" className="gap-1.5">
                  History
                  {tabCounts.history != null ? (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 z-caption z-numeric text-muted-foreground">
                      {tabCounts.history}
                    </span>
                  ) : null}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          {adminError ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <RefreshCcw className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <p className="z-body-md text-foreground">
                  Unable to load appointments
                </p>
                <p className="z-secondary text-muted-foreground">
                  {adminError}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="z-button"
              >
                Try again
              </Button>
            </div>
          ) : (
            <>
              {!isLoading && appointments.length > 0 ? (
                <div className="flex items-center justify-between border-b border-border/60 bg-card px-5 py-3 z-secondary text-muted-foreground">
                  <p>
                    Showing{" "}
                    <span className="z-numeric font-medium text-foreground">
                      {appointments.length}
                    </span>{" "}
                    of{" "}
                    <span className="z-numeric font-medium text-foreground">
                      {totalCount}
                    </span>{" "}
                    appointment{totalCount === 1 ? "" : "s"}
                  </p>
                  <p>
                    Page{" "}
                    <span className="z-numeric font-medium text-foreground">
                      {page}
                    </span>{" "}
                    of{" "}
                    <span className="z-numeric font-medium text-foreground">
                      {totalPages}
                    </span>
                  </p>
                </div>
              ) : null}

              <div className="min-h-0 flex-1 overflow-auto p-4">
                <div className="overflow-hidden rounded-3xl border border-border/60 bg-card shadow-sm">
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-card">
                      <TableRow className="hover:bg-card">
                        <TableHead className="min-w-[200px]">
                          <SortButton
                            label="Patient"
                            isActive={sortField === "patient"}
                            direction={sortDir}
                            onClick={() => handleSort("patient")}
                          />
                        </TableHead>
                        <TableHead className="hidden lg:table-cell">
                          <SortButton
                            label="Doctor"
                            isActive={sortField === "doctor"}
                            direction={sortDir}
                            onClick={() => handleSort("doctor")}
                          />
                        </TableHead>
                        <TableHead className="hidden lg:table-cell z-label uppercase tracking-wide text-muted-foreground">
                          Service
                        </TableHead>
                        <TableHead>
                          <SortButton
                            label="Date & Time"
                            isActive={sortField === "date"}
                            direction={sortDir}
                            onClick={() => handleSort("date")}
                          />
                        </TableHead>
                        <TableHead>
                          <SortButton
                            label="Status"
                            isActive={sortField === "status"}
                            direction={sortDir}
                            onClick={() => handleSort("status")}
                          />
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading ? (
                        <LoadingRows showDoctor />
                      ) : appointments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="h-40 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                                <CalendarClock className="h-5 w-5" />
                              </div>
                              <div className="space-y-1">
                                <p className="z-body-md text-foreground">
                                  {search || hasActiveFilters
                                    ? "No appointments found"
                                    : "No appointments yet"}
                                </p>
                                <p className="z-secondary text-muted-foreground">
                                  {search || hasActiveFilters
                                    ? "Try a broader search or clear the active filter."
                                    : "Appointments will appear here once created."}
                                </p>
                              </div>
                              {hasActiveFilters || search ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSearch("");
                                    clearFilters();
                                  }}
                                  className="z-button"
                                >
                                  Clear filters
                                </Button>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        appointments.map((appt) => (
                          <TableRow
                            key={appt.id}
                            className="cursor-pointer bg-card transition-colors hover:bg-muted/20"
                            onClick={() => {
                              setSelectedAppointmentId(appt.id);
                              setDetailsOpen(true);
                            }}
                          >
                            <TableCell className="py-3">
                              <div className="space-y-0.5">
                                <p className="truncate z-body-md text-foreground">
                                  {appt.patient_name || "Unknown patient"}
                                </p>
                                {appt.patient_zarity_id ? (
                                  <p className="z-caption z-numeric text-muted-foreground">
                                    #{appt.patient_zarity_id}
                                  </p>
                                ) : null}
                              </div>
                            </TableCell>

                            <TableCell className="hidden lg:table-cell py-3 z-body text-foreground">
                              {appt.doctor_name || "—"}
                            </TableCell>

                            <TableCell className="hidden lg:table-cell py-3 z-body text-foreground">
                              <div className="space-y-0.5">
                                <p className="truncate">
                                  {appt.service_name || "—"}
                                </p>
                                {appt.consultation_type || appt.clinic ? (
                                  <p className="z-caption text-muted-foreground truncate">
                                    {appt.clinic
                                      ? `In-Person — ${appt.clinic.name}`
                                      : appt.consultation_type}
                                  </p>
                                ) : null}
                              </div>
                            </TableCell>

                            <TableCell className="py-3">
                              <div className="space-y-1.5">
                                <p className="z-body text-foreground">
                                  {formatShortDate(appt.start_time)}
                                </p>
                                <Badge
                                  variant="outline"
                                  className="rounded-full border-border/70 bg-muted/40 px-2 py-0.5 z-caption z-numeric font-normal text-muted-foreground shadow-none"
                                >
                                  {formatTime(appt.start_time)}
                                  {appt.end_time
                                    ? ` – ${formatTime(appt.end_time)}`
                                    : ""}
                                </Badge>
                              </div>
                            </TableCell>

                            <TableCell className="py-3">
                              <Badge
                                variant="outline"
                                className={cn(
                                  "rounded-full px-2.5 py-1 z-caption shadow-none",
                                  appointmentStatusClass(appt.status)
                                )}
                              >
                                {getStatusLabel(appt.status)}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {totalPages > 1 ? (
                <div className="flex items-center justify-between border-t border-border/60 px-5 py-4">
                  <p className="z-secondary text-muted-foreground">
                    Showing page{" "}
                    <span className="z-numeric text-foreground">{page}</span> of{" "}
                    <span className="z-numeric text-foreground">
                      {totalPages}
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1 || isFetching}
                      onClick={() =>
                        setPage((current) => Math.max(1, current - 1))
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages || isFetching}
                      onClick={() =>
                        setPage((current) => Math.min(totalPages, current + 1))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <AppointmentDetailsDialog
        appointmentId={selectedAppointmentId}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        onAppointmentCancelled={refetch}
      />
    </div>
  );
};
